import { useEffect, useRef } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Button, Icon } from '@nazka/nazka.mapframe.components'
import { faClose, faLocation } from '@fortawesome/pro-light-svg-icons'

import { fetchMissionDataSourcesByGroup, deleteMissionData } from '@services/data.service.js'
import DataSectionTitle from '@App/components/DataSectionTitle/DataSectionTitle'
import SourceToggles from '@App/components/SourceToggles/SourceToggles'
import ActionBtn from '@App/components/ActionBtn/ActionBtn'
import { useAuth } from '@App/components/AuthProvider/AuthProvider'

import { setHoveredItem, setClickedItem, setSourceActiveNames } from '../alerts.slice'

import './alert-overview.styl'

function AlertOverview() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const alertListRef = useRef(null)
  const { user } = useAuth()

  const { map, alerts, refetchAlerts, alertDataTypes } = useOutletContext()

  const { activeProject } = useSelector(state => state.missionSetup)
  const { sourcesActiveNames, hoveredItem, clickedItem } = useSelector(state => state.alerts)

  const { data: sources } = useQuery({
    queryKey: ['sources', activeProject?.id, 'alert'],
    queryFn: () => fetchMissionDataSourcesByGroup(activeProject?.id, 'alert'),
  })

  useEffect(() => {
    if (!clickedItem) return
    const specimenElement = document.getElementById(`alert-${clickedItem.id}`)
    if (specimenElement && alertListRef.current) {
      const rect = specimenElement.getBoundingClientRect()
      const listRect = alertListRef.current.getBoundingClientRect()
      const scrollTop = rect.top - listRect.top + alertListRef.current.scrollTop
      alertListRef.current.scrollTo({
        top: scrollTop - 2,
        behavior: alerts?.length < 20 ? 'smooth' : 'auto',
      })
      dispatch(setClickedItem(null))
    }
  }, [clickedItem, dispatch, alerts])

  useEffect(() => {
    if (!map) return
    const canvasContainer = document.querySelector('.maplibregl-canvas')

    function setHighlightedFeature(e) {
      if (e?.features?.length) {
        const feature = e.features[0]
        canvasContainer.classList.add('pointer-cursor')
        dispatch(setHoveredItem(feature?.properties))
      }
      else {
        dispatch(setHoveredItem(null))
      }
    }

    function setClickedFeature(e) {
      if (e?.features?.length) {
        const feature = e.features[0]
        dispatch(setClickedItem(feature?.properties))
      }
      else {
        dispatch(setClickedItem(null))
      }
    }

    const layers = ['alerts-layer']

    map.on('mousemove', layers, setHighlightedFeature)
    map.on('mouseleave', layers, () => {
      canvasContainer.classList.remove('pointer-cursor')
      dispatch(setHoveredItem(null))
    })
    map.on('click', layers, setClickedFeature)

    return () => {
      if (map) {
        canvasContainer.classList.remove('pointer-cursor')
        map.off('mousemove', layers, setHighlightedFeature)
        map.off('mouseleave', layers, () => dispatch(setHoveredItem(null)))
        map.off('click', layers, setClickedFeature)
        dispatch(setHoveredItem(null))
      }
    }
  }, [map, dispatch])

  useEffect(() => {
    if (!map) return
    const hoveredAlertSource = map.getSource('hovered-alert')

    if (hoveredItem) {
      const data = map.getSource('alerts')?._data
      const feature = data?.features.find(f => f.properties.id === hoveredItem.id)
      if (feature) {
        hoveredAlertSource?.setData({
          type: 'FeatureCollection',
          features: [feature],
        })
      }
    }
    else {
      hoveredAlertSource?.setData({
        type: 'FeatureCollection',
        features: [],
      })
    }

    return () => {
      const hoveredAlertSource = map?.getSource('hovered-alert')
      hoveredAlertSource?.setData({
        type: 'FeatureCollection',
        features: [],
      })
    }
  }, [map, hoveredItem])

  return (
    <div className="alert-overview">
      <div className="environment__action">
        <ActionBtn onClick={() => navigate('/alerts/add-alert')}>
          Add new alert
        </ActionBtn>
      </div>
      <hr />
      {sources && sources.length !== 0
        ? (
            <>
              <DataSectionTitle>Data source</DataSectionTitle>
              <SourceToggles
                sources={sources}
                sourcesActiveNames={sourcesActiveNames}
                setSourceActiveNames={setSourceActiveNames}
              />
              <hr />
            </>
          )
        : (
            <p className="alert-overview__no-data">
              No current alerts for this mission.
            </p>
          )}
      <div className="alerts__content">
        <ul className="alerts__content__alerts" ref={alertListRef}>
          {alerts?.filter(alert => sourcesActiveNames.includes(alert.properties?.source)).map((alert) => {
            const alertId = alert.properties?.id
            const alertDataType = alert.properties?.dataType
            const isHovered = hoveredItem && hoveredItem.id === alertId

            return (
              <li
                className={`alerts__content__alerts__alert${isHovered ? ' alerts__content__alerts__alert--hovered' : ''}`}
                onMouseEnter={() => dispatch(setHoveredItem(alert.properties))}
                onMouseLeave={() => dispatch(setHoveredItem(null))}
                id={`alert-${alertId}`}
                key={alertId}
              >
                <div className="alerts__content__alerts__alert__header">
                  <div className="alerts__content__alerts__alert__header__title">
                    <Icon
                      icon="/img/icons/triangle-exclamation.svg"
                      rootElProps={{
                        css: {
                          svg: {
                            fill: alertDataTypes?.find(type => type.id === alertDataType)?.color || '#000000',
                          },
                        },
                      }}
                      className="alerts__content__alerts__alert__header__title__icon"
                    />
                    <div className="alerts__content__alerts__alert__header__title__text">
                      <p className="alerts__content__alerts__alert__header__title__text__title">
                        Warning
                      </p>
                      <p className="alerts__content__alerts__alert__header__title__text__subtitle">
                        {new Date(alert.properties?.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="alerts__content__alerts__alert__header__buttons">
                    <Button
                      icon={faLocation}
                      iconSize="20px"
                      className="alerts__content__alerts__alert__header__buttons__button"
                      layout="ghost"
                      hover={false}
                      onClick={() => {
                        if (map) {
                          const { geometry } = alert
                          if (geometry) {
                            map.flyTo({
                              center: geometry.coordinates,
                              zoom: 14,
                              essential: true,
                            })
                          }
                        }
                      }}
                    />
                    {alert.properties?.owner === user?.sub && (
                      <Button
                        icon={faClose}
                        iconSize="20px"
                        className="alerts__content__alerts__alert__header__buttons__button"
                        layout="ghost"
                        hover={false}
                        onClick={() => {
                          const hoveredAlertSource = map.getSource('hovered-alert')
                          hoveredAlertSource?.setData({
                            type: 'FeatureCollection',
                            features: [],
                          })

                          deleteMissionData(alertId)
                          refetchAlerts()
                        }}
                      />
                    )}
                  </div>
                </div>
                {alert?.properties?.description && (
                  <div className="alerts__content__alerts__alert__body">
                    {alert.properties.description}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default AlertOverview
